<template>
  <div class="withdraw-page">
    <header class="nav-header">
      <button class="nav-back" type="button" @click="back"><van-icon name="arrow-left" /></button>
      <div class="nav-title">{{ $t('cryptoWithdraw.title') }}</div>
      <button class="nav-record" type="button" @click="$router.push('/WithdrawRecord')">{{ $t('cryptoWithdraw.record') }}</button>
    </header>

    <main class="content">
      <section class="crypto-hero">
        <div class="crypto-mark">₮</div>
        <div>
          <h1>{{ $t('cryptoWithdraw.assetTitle') }}</h1>
          <p>{{ $t('cryptoWithdraw.assetSubtitle') }}</p>
        </div>
        <van-icon name="shield-o" class="hero-shield" />
      </section>

      <section class="crypto-card">
        <div class="field-block">
          <label>{{ $t('cryptoWithdraw.selectCoin') }}</label>
          <div class="coin-options">
            <button v-for="coin in cryptoCoins" :key="coin.code" type="button" class="coin-option"
              :class="{ active: selectedCoin === coin.code }" @click="selectCoin(coin.code)">
              <span class="coin-symbol">{{ coin.symbol }}</span><span>{{ coin.code }}</span>
            </button>
          </div>
        </div>

        <div class="field-block">
          <label>{{ $t('cryptoWithdraw.selectNetwork') }}</label>
          <div class="network-options">
            <button v-for="network in availableNetworks" :key="network" type="button" class="network-option"
              :class="{ active: selectedNetwork === network }" @click="selectNetwork(network)">
              {{ network }}
            </button>
          </div>
        </div>

        <div class="field-block">
          <div class="address-label-row">
            <label>{{ $t('cryptoWithdraw.walletAddress') }}</label>
            <button type="button" class="bind-address-btn" @click="openAddressBinding">
              <van-icon name="plus" /> {{ addressText.bind }}
            </button>
          </div>
          <div v-if="matchingAddresses.length" class="saved-addresses">
            <button v-for="item in matchingAddresses" :key="item.id" type="button"
              :class="{ active: walletAddress === item.bankid }" @click="useSavedAddress(item)">
              <span>{{ shortAddress(item.bankid) }}</span><em>{{ item.bankfenhang }}</em>
            </button>
          </div>
          <div class="wallet-input-wrap">
            <van-icon name="wallet-o" />
            <input v-model.trim="walletAddress" class="wallet-field" type="text" :placeholder="$t('cryptoWithdraw.walletPlaceholder')" />
            <van-icon name="scan" class="scan-icon" @click="openQrScanner" />
            <input ref="qrFileInput" class="qr-file-input" type="file" accept="image/*"
              capture="environment" @change="decodeQrImage" />
          </div>
          <p class="field-tip">{{ $t('cryptoWithdraw.addressTip', { network: selectedNetwork }) }}</p>
        </div>

        <div class="field-block">
          <label>{{ $t('cryptoWithdraw.amount') }}</label>
          <div class="amount-input">
            <span class="currency">$</span>
            <input v-model="withdraw_money" class="amount-field" type="number" min="0" :step="amountStep" placeholder="0.00" />
            <button type="button" class="all-btn" @click="allMoney">{{ $t('cryptoWithdraw.all') }}</button>
          </div>
          <div class="balance-row">
            <span>{{ $t('cryptoWithdraw.availableBalance') }}</span><strong>{{ userInfo.money || '0.00' }} $</strong>
          </div>
        </div>

        <div class="fee-summary">
          <div><span>{{ $t('cryptoWithdraw.network') }}</span><strong>{{ selectedNetwork }}</strong></div>
          <div><span>{{ $t('cryptoWithdraw.fee') }}</span><strong>{{ estimatedFee }} $</strong></div>
          <div class="arrival"><span>{{ $t('cryptoWithdraw.arrival') }}</span><strong>{{ estimatedArrival }} $</strong></div>
        </div>
      </section>

      <button class="submit-btn" type="button" :disabled="submitting" @click="doWithdraw">
        {{ submitting ? $t('cryptoWithdraw.submitting') : $t('cryptoWithdraw.confirm') }}
      </button>
      <button class="rules-btn" type="button" @click="showRules = true"><van-icon name="question-o" /> {{ $t('cryptoWithdraw.safetyInfo') }}</button>
      <p class="security-tip"><van-icon name="shield-o" /> {{ $t('cryptoWithdraw.safetyCaption') }}</p>
    </main>

    <van-dialog v-model="showDialog" :title="$t('cryptoWithdraw.verification')" :confirm-button-text="$t('cryptoWithdraw.confirm')" :cancel-button-text="$t('cryptoWithdraw.cancel')"
      show-cancel-button :before-close="beforeConfirm">
      <div class="password-box">
        <p>{{ $t('cryptoWithdraw.passwordPrompt') }}</p>
        <van-field v-model="paypsd" type="password" :label="$t('cryptoWithdraw.payPassword')" :placeholder="$t('cryptoWithdraw.passwordPlaceholder')" />
      </div>
    </van-dialog>

    <van-action-sheet v-model="showRules" :title="$t('cryptoWithdraw.rulesTitle')">
      <div class="rules-content">
        <p>1. {{ $t('cryptoWithdraw.rule1') }}</p>
        <p>2. {{ $t('cryptoWithdraw.rule2') }}</p>
        <p>3. {{ $t('cryptoWithdraw.rule3') }}</p>
        <p>4. {{ $t('cryptoWithdraw.rule4') }}</p>
      </div>
    </van-action-sheet>

  </div>
</template>

<script>
import jsQR from 'jsqr';

export default {
  name: 'WithdrawPage',
  data() {
    return {
      userInfo: {},
      withdraw_money: '',
      walletAddress: '',
      savedAddresses: [],
      selectedCoin: 'USDT',
      selectedNetwork: 'TRC20',
      cryptoCoins: [
        { code: 'USDT', symbol: '₮', networks: ['TRC20', 'ERC20', 'BEP20'], fee: 1 },
        { code: 'USDC', symbol: '$', networks: ['ERC20', 'BEP20'], fee: 1 },
        { code: 'BTC', symbol: '₿', networks: ['Bitcoin'], fee: 0.0002 },
        { code: 'ETH', symbol: 'Ξ', networks: ['ERC20', 'BEP20'], fee: 0.003 }
      ],
      bankId: 0,
      showDialog: false,
      showRules: false,
      paypsd: '',
      submitting: false
    };
  },
  computed: {
    currentCoin() {
      return this.cryptoCoins.find(item => item.code === this.selectedCoin) || this.cryptoCoins[0];
    },
    availableNetworks() {
      return this.currentCoin.networks;
    },
    matchingAddresses() {
      return this.savedAddresses.filter(item =>
        String(item.bankinfo || '').toUpperCase() === this.selectedCoin &&
        String(item.bankfenhang || '').toUpperCase() === String(this.selectedNetwork || '').toUpperCase()
      );
    },
    addressText() {
      return this.$t('cryptoAddress');
    },
    scanText() {
      const t = this.$t('cryptoAddress');
      return { success:t.scanSuccess, notFound:t.scanFail, invalid:t.scanFail };
    },
    estimatedFee() {
      return this.currentCoin.fee;
    },
    amountStep() {
      return ['BTC', 'ETH'].includes(this.selectedCoin) ? '0.000001' : '0.01';
    },
    estimatedArrival() {
      const value = Number(this.withdraw_money || 0) - Number(this.estimatedFee || 0);
      const digits = ['BTC', 'ETH'].includes(this.selectedCoin) ? 6 : 2;
      return Math.max(value, 0).toFixed(digits);
    }
  },
  methods: {
    back() { this.$router.back(); },
    selectCoin(code) {
      this.selectedCoin = code;
      this.selectedNetwork = this.availableNetworks[0];
      this.applyMatchingAddress();
    },
    selectNetwork(network) {
      this.selectedNetwork = network;
      this.applyMatchingAddress();
    },
    openAddressBinding() {
      this.$router.push({ path:'/BindCryptoAddress', query:{ coin:this.selectedCoin, network:this.selectedNetwork } });
    },
    openRequiredAddressBinding() {
      this.$router.push({ path:'/BindCryptoAddress', query:{ coin:this.selectedCoin, network:this.selectedNetwork, required:'1' } });
    },
    shortAddress(address) {
      const value = String(address || '');
      return value.length > 16 ? `${value.slice(0, 8)}...${value.slice(-6)}` : value;
    },
    openQrScanner() {
      const input = this.$refs.qrFileInput;
      if (!input) return;
      input.value = '';
      input.click();
    },
    decodeQrImage(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = loadEvent => {
        const image = new Image();
        image.onload = () => {
          const maxSize = 1600;
          const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.width * scale));
          canvas.height = Math.max(1, Math.round(image.height * scale));
          const context = canvas.getContext('2d');
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
          const result = jsQR(pixels.data, pixels.width, pixels.height, { inversionAttempts: 'attemptBoth' });
          if (!result || !result.data) return this.$toast(this.scanText.notFound);
          const address = this.extractWalletAddress(result.data);
          if (!address) return this.$toast(this.scanText.invalid);
          this.walletAddress = address;
          this.$toast.success(this.scanText.success);
        };
        image.onerror = () => this.$toast(this.scanText.notFound);
        image.src = loadEvent.target.result;
      };
      reader.readAsDataURL(file);
    },
    extractWalletAddress(rawValue) {
      let value = String(rawValue || '').trim();
      try { value = decodeURIComponent(value); } catch (e) { value = String(rawValue || '').trim(); }
      value = value.replace(/^(ethereum|bitcoin|bitcoincash|tron):/i, '');
      value = value.split('?')[0].split('#')[0].replace(/^\/\//, '').trim();
      const evm = value.match(/0x[a-fA-F0-9]{40}/);
      if (evm) return evm[0];
      const tron = value.match(/T[1-9A-HJ-NP-Za-km-z]{25,34}/);
      if (tron) return tron[0];
      const bitcoin = value.match(/(?:bc1|[13])[a-zA-HJ-NP-Z0-9]{20,62}/);
      return bitcoin ? bitcoin[0] : '';
    },
    useSavedAddress(item) {
      this.walletAddress = item.bankid || '';
    },
    applyMatchingAddress() {
      this.$nextTick(() => {
        if (this.matchingAddresses.length) this.walletAddress = this.matchingAddresses[0].bankid || '';
        else this.walletAddress = '';
      });
    },
    allMoney() { this.withdraw_money = this.userInfo.money || ''; },
    getUserInfo() {
      this.$http({ method: 'get', url: 'user_info' }).then(res => {
        if (res.code === 200) this.userInfo = res.data || {};
      });
    },
    getWithdrawAccount() {
      this.$http({ method: 'get', url: 'user_get_withdraw_role' }).then(res => {
        if (res.code !== 200) return;
        const payload = res.data || {};
        const list = payload.list || (payload.data && payload.data.list) || [];
        if (list.length) this.bankId = Number(list[0].value || list[0].id || 0);
      }).catch(() => {});
    },
    getSavedAddresses() {
      this.$http({ method: 'get', url: 'user_get_bank' }).then(res => {
        if (res.code !== 200) return;
        this.savedAddresses = Array.isArray(res.data && res.data.crypto_list) ? res.data.crypto_list : [];
        this.applyMatchingAddress();
      }).catch(() => {});
    },
    isValidAddress() {
      return String(this.walletAddress || '').trim().length > 0;
    },
    doWithdraw() {
      if (!this.matchingAddresses.length) {
        this.openRequiredAddressBinding();
        return;
      }
      if (!this.matchingAddresses.some(item => item.bankid === this.walletAddress)) {
        this.walletAddress = this.matchingAddresses[0].bankid || '';
      }
      this.validateAndOpenWithdraw();
    },
    validateAndOpenWithdraw() {
      const amount = Number(this.withdraw_money);
      if (!this.walletAddress) return this.$toast(this.$t('cryptoWithdraw.enterAddress'));
      if (!this.isValidAddress()) return this.$toast(this.$t('cryptoWithdraw.invalidAddress'));
      if (!amount || amount <= Number(this.estimatedFee)) return this.$toast(this.$t('cryptoWithdraw.invalidAmount'));
      if (amount > Number(this.userInfo.money || 0)) return this.$toast(this.$t('cryptoWithdraw.insufficientBalance'));
      this.showDialog = true;
    },
    beforeConfirm(action, done) {
      if (action === 'cancel') { done(); return; }
      if (!this.paypsd) { this.$toast(this.$t('cryptoWithdraw.enterPassword')); done(false); return; }
      this.submitting = true;
      this.submitWithdraw(done);
    },
    submitWithdraw(done) {
      this.$http({
        method: 'post',
        url: 'user_set_withdraw',
        data: {
          money: this.withdraw_money,
          bankid: this.bankId,
          address_id: this.bankId,
          paypassword: this.paypsd,
          coin: this.selectedCoin,
          network: this.selectedNetwork,
          wallet_address: this.walletAddress
        }
      }).then(res => {
        this.submitting = false;
        if (res.code === 200) {
          done();
          this.$toast.success(res.msg || this.$t('cryptoWithdraw.success'));
          setTimeout(() => this.$router.push('/WithdrawRecord'), 1200);
        } else {
          this.$toast(res.msg || this.$t('cryptoWithdraw.failed'));
          done(false);
        }
      }).catch(() => { this.submitting = false; done(false); });
    }
  },
  created() {
    if (!localStorage.getItem('token')) return this.$router.push('/Login');
    if (this.$route.query.coin) this.selectedCoin = String(this.$route.query.coin).toUpperCase();
    if (this.$route.query.network) {
      const requestedNetwork = String(this.$route.query.network);
      const matchedNetwork = this.availableNetworks.find(item =>
        String(item).toUpperCase() === requestedNetwork.toUpperCase()
      );
      this.selectedNetwork = matchedNetwork || this.availableNetworks[0];
    } else {
      this.selectedNetwork = this.availableNetworks[0];
    }
    this.getUserInfo();
    this.getWithdrawAccount();
    this.getSavedAddresses();
  }
};
</script>

<style lang="less" scoped>
.withdraw-page { min-height: 100vh; color: #f3eadb; background: linear-gradient(rgba(3,5,8,.82),rgba(3,5,8,.96)), url('../../assets/img/luxury-dating-bg-v2.png') center top/cover fixed no-repeat; }
.nav-header { height: 4.5rem; padding: 0 1rem; display: grid; grid-template-columns: 5.5rem minmax(0, 1fr) 5.5rem; align-items: center; border-bottom: 1px solid rgba(218,180,105,.4); background: rgba(4,6,9,.96); position: sticky; top: 0; z-index: 5; box-shadow: 0 .5rem 1.5rem rgba(0,0,0,.34); }
.nav-back,.nav-record { border: 0; color: #e8ca85; background: transparent; cursor: pointer; }
.nav-back { min-height: 3rem; display: flex; align-items: center; text-align: left; font-size: 1.75rem; }.nav-record { min-height: 3rem; padding: 0; text-align: right; white-space: nowrap; font-size: .96rem; font-weight: 700; }.nav-title { overflow: visible; text-align: center; white-space: nowrap; color: #efd18a; font-size: 1.28rem; font-weight: 750; letter-spacing: .04em; }
.content { padding: 15px 13px 38px; }.crypto-hero { display: flex; align-items: center; gap: 13px; padding: 18px; margin-bottom: 14px; border: 1px solid rgba(218,180,105,.34); border-radius: 20px; background: linear-gradient(145deg,rgba(25,20,12,.94),rgba(7,10,14,.96)); box-shadow: 0 14px 34px rgba(0,0,0,.4); }
.crypto-mark { width: 48px; height: 48px; display: grid; place-items: center; border-radius: 50%; color: #171006; font-size: 1.55rem; font-weight: 800; background: linear-gradient(135deg,#a97732,#ebcf86); }.crypto-hero h1 { margin: 0 0 5px; color: #f0d28d; font-size: 1.18rem; }.crypto-hero p { margin: 0; color: #bdb19e; font-size: .78rem; }.hero-shield { margin-left: auto; color: #d9b66e; font-size: 1.5rem; }
.crypto-card { padding: 20px 17px; border: 1px solid rgba(218,180,105,.34); border-radius: 22px; background: linear-gradient(145deg,rgba(15,18,23,.96),rgba(6,8,12,.97)); box-shadow: 0 18px 42px rgba(0,0,0,.45); }.field-block { margin-bottom: 22px; }.field-block>label { display: block; margin-bottom: 12px; color: #e5c47d; font-size: .94rem; font-weight: 700; }
.coin-options,.network-options { display: flex; gap: 9px; flex-wrap: wrap; }.coin-option,.network-option { border: 1px solid rgba(218,180,105,.3); color: #c9bda9; background: rgba(19,21,25,.9); cursor: pointer; }.coin-option { min-width: 91px; padding: 10px 13px; display: flex; align-items: center; gap: 8px; border-radius: 13px; font-size: 1rem; font-weight: 700; }.network-option { padding: 9px 15px; border-radius: 999px; font-size: .78rem; font-weight: 600; }.coin-option.active,.network-option.active { color: #171006; border-color: #efd18a; background: linear-gradient(110deg,#a97732,#e7c87c); }.coin-symbol { width: 25px; height: 25px; display: grid; place-items: center; border-radius: 50%; font-size: .9rem; background: rgba(255,255,255,.14); }
.wallet-input-wrap,.amount-input { min-height: 56px; display: flex; align-items: center; gap: 10px; padding: 0 14px; border: 1px solid rgba(218,180,105,.28); border-radius: 14px; background: rgba(3,5,8,.62); }.wallet-input-wrap>.van-icon { color: #d7b56d; font-size: 1.2rem; }.wallet-field,.amount-field { min-width: 0; flex: 1; border: 0; outline: 0; color: #f2eadc; background: transparent; font-size: .9rem; }.wallet-field::placeholder,.amount-field::placeholder { color: #716b61; }.scan-icon { cursor: pointer; }.field-tip { margin: 9px 2px 0; color: #a19889; font-size: .72rem; line-height: 1.55; }.currency { color: #e7c67e; font-size: .86rem; font-weight: 700; }.amount-field { font-size: 1.4rem; }.all-btn { border: 0; color: #e8c980; background: transparent; font-size: .9rem; font-weight: 700; cursor: pointer; }.balance-row { display: flex; justify-content: space-between; margin-top: 10px; color: #a79c8c; font-size: .75rem; }.balance-row strong { color: #d7c7ad; }
.qr-file-input { position:absolute; width:1px; height:1px; overflow:hidden; opacity:0; pointer-events:none; }
.address-label-row { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.address-label-row label { color:#e5c47d; font-size:.94rem; font-weight:700; }
.bind-address-btn { padding:6px 9px; border:1px solid rgba(218,180,105,.36); border-radius:9px; color:#e8c980; background:rgba(216,177,99,.08); font-size:.72rem; }
.saved-addresses { display:flex; gap:7px; margin-bottom:10px; overflow-x:auto; }
.saved-addresses button { min-width:8.8rem; padding:8px 10px; border:1px solid rgba(218,180,105,.25); border-radius:10px; color:#c8baa5; background:#0d1015; text-align:left; }
.saved-addresses button.active { border-color:#edcf87; color:#171006; background:linear-gradient(110deg,#a97732,#e7c87c); }
.saved-addresses span,.saved-addresses em { display:block; }.saved-addresses em { margin-top:3px; font-size:.65rem; font-style:normal; opacity:.8; }
.bind-address-dialog { padding:12px 16px 20px; background:#090c11; }.bind-address-dialog p { color:#edcf87; text-align:center; }
.fee-summary { padding: 14px; border: 1px solid rgba(218,180,105,.18); border-radius: 14px; background: rgba(216,177,99,.055); }.fee-summary>div { display: flex; justify-content: space-between; padding: 8px 0; color: #afa493; font-size: .8rem; }.fee-summary strong { color: #ddd0bb; font-size: .82rem; }.fee-summary .arrival { margin-top: 5px; padding-top: 12px; border-top: 1px solid rgba(218,180,105,.16); }.fee-summary .arrival strong { color: #edcf88; font-size: 1rem; }
.submit-btn { width: 100%; min-height: 54px; margin-top: 18px; border: 1px solid rgba(247,220,160,.68); border-radius: 15px; color: #171006; font-size: 1rem; font-weight: 750; background: linear-gradient(110deg,#a97732,#e7c87c,#bb853c); box-shadow: 0 10px 26px rgba(185,130,48,.3); cursor: pointer; }.submit-btn:disabled { opacity: .6; }.rules-btn { display: block; margin: 16px auto 0; border: 0; color: #c7b99f; font-size: .9rem; background: transparent; cursor: pointer; }.security-tip { text-align: center; color: #8f8576; font-size: .72rem; }.password-box { padding: 10px 15px 18px; }.password-box p { color: #aaa08f; font-size: .82rem; }.rules-content { padding: 8px 22px 30px; color: #c7bba8; font-size: .82rem; line-height: 1.7; }

@media (max-width:430px){
  .withdraw-page{width:100%;min-width:0;overflow-x:hidden;background-attachment:scroll}
  .nav-header{width:100%;height:auto;min-height:62px;padding:env(safe-area-inset-top) 10px 0;grid-template-columns:44px minmax(0,1fr) minmax(70px,auto)}
  .nav-back{min-width:44px;min-height:52px;padding:0;font-size:23px}
  .nav-title{min-width:0;padding:8px 4px;font-size:17px;line-height:1.25;letter-spacing:0;white-space:normal;overflow-wrap:anywhere}
  .nav-record{max-width:108px;min-height:44px;padding:5px 0 5px 6px;font-size:13px;line-height:1.25;white-space:normal;overflow-wrap:anywhere}
  .content{min-width:0;padding:12px 10px max(26px,env(safe-area-inset-bottom))}
  .crypto-hero{min-width:0;gap:11px;padding:15px 13px;border-radius:17px}
  .crypto-mark{width:43px;height:43px;flex:0 0 43px}
  .crypto-hero>div:nth-child(2){min-width:0}
  .crypto-hero h1,.crypto-hero p{max-width:100%;line-height:1.35;overflow-wrap:anywhere}
  .hero-shield{flex:0 0 auto}
  .crypto-card{min-width:0;padding:17px 13px;border-radius:18px}
  .field-block>label,.address-label-row label{line-height:1.35;overflow-wrap:anywhere}
  .coin-options,.network-options{gap:8px}
  .coin-option{min-width:0;flex:1 1 calc(50% - 4px);padding:10px}
  .network-option{max-width:100%;padding:9px 12px;font-size:13px;line-height:1.25;overflow-wrap:anywhere}
  .address-label-row{min-width:0;gap:8px;align-items:flex-start}
  .address-label-row label{min-width:0;flex:1}
  .bind-address-btn{max-width:48%;height:auto;padding:6px 8px;font-size:13px;line-height:1.3;white-space:normal;overflow-wrap:anywhere}
  .saved-addresses{max-width:100%;padding-bottom:3px;-webkit-overflow-scrolling:touch}
  .wallet-input-wrap,.amount-input{min-width:0;gap:7px;padding:0 10px}
  .wallet-field,.amount-field{width:0;min-width:0;font-size:16px}
  .amount-field{font-size:21px}
  .all-btn{flex:0 0 auto;max-width:34%;padding:7px 0 7px 5px;line-height:1.25;white-space:normal;overflow-wrap:anywhere}
  .field-tip{overflow-wrap:anywhere}
  .balance-row{gap:8px;flex-wrap:wrap;line-height:1.35}
  .balance-row span,.balance-row strong{min-width:0;overflow-wrap:anywhere}
  .fee-summary{padding:12px}
  .fee-summary>div{gap:10px;align-items:flex-start}
  .fee-summary span{min-width:0;line-height:1.35;overflow-wrap:anywhere}
  .fee-summary strong{flex:0 0 auto;max-width:46%;line-height:1.35;text-align:right;overflow-wrap:anywhere}
  .submit-btn,.rules-btn{height:auto;line-height:1.35;white-space:normal;overflow-wrap:anywhere}
  .security-tip{line-height:1.45;overflow-wrap:anywhere}
  ::v-deep .van-dialog{width:calc(100vw - 28px);max-width:370px;border-radius:16px;overflow:hidden}
  .password-box{padding:10px 12px 16px}
  .password-box p{line-height:1.45;overflow-wrap:anywhere}
  .password-box ::v-deep .van-field{display:block;padding:12px}
  .password-box ::v-deep .van-field__label{width:100%;margin:0 0 6px;line-height:1.35;overflow-wrap:anywhere}
  .password-box ::v-deep .van-field__value,.password-box ::v-deep .van-field__body,.password-box ::v-deep .van-field__control{width:100%;min-width:0}
  ::v-deep .van-dialog__footer .van-button{height:auto;min-height:50px;padding:8px 5px;line-height:1.3;white-space:normal}
  ::v-deep .van-action-sheet{max-height:82svh}
  .rules-content{padding:8px 16px max(22px,env(safe-area-inset-bottom))}
  .rules-content p{overflow-wrap:anywhere}
}

@media (max-width:340px){
  .content{padding-right:8px;padding-left:8px}
  .crypto-card{padding-right:11px;padding-left:11px}
  .nav-record{max-width:88px;font-size:12px}
}
</style>
